/**
 * MASAL Product Page V2
 * File: assets/masal-product.js
 *
 * Responsibilities
 * ----------------
 * 1. Keep selected Shopify variant in sync across MASAL ProductPageV2.
 * 2. Update contextual retail / B2B price after variant changes.
 * 3. Add the main gown + selected add-ons to cart in one request.
 * 4. Keep purchase CTA state in sync with variant availability.
 * 5. Provide product media lightbox / expand behaviour.
 * 6. Support Shopify Theme Editor section reloads.
 *
 * Important pricing rule
 * ----------------------
 * Guest users receive no price behaviour because guest price markup/data
 * should not be rendered by the Liquid snippets.
 *
 * Logged-in retail and B2B prices are read only from Liquid-rendered Shopify
 * contextual variant data. No wholesale price is hard-coded in JavaScript.
 */

(() => {
  'use strict';

  const SELECTORS = {
    productRoot: '[data-masal-product-root], .masal-pdp',
    productForm: '[data-masal-product-form]',
    variantPicker: '[data-masal-variant-picker]',
    variantInput: '[data-masal-variant-input]',
    variantsJson: '[data-masal-variants-json]',
    price: '[data-masal-price]',
    comparePrice: '[data-masal-compare-price]',
    addButton: '[data-masal-add-button]',
    addLabel: '[data-masal-add-label]',
    formMessage: '[data-masal-form-message]',
    addons: '[data-masal-addons]',
    addon: '[data-masal-addon]',
    addonVariantSelect: '[data-masal-addon-variant-select]',
    mediaItem: '[data-masal-media-item]',
    mediaExpand: '[data-masal-media-expand]',
    finalCta: '[data-masal-final-cta]',
    returnToPurchase: '[data-masal-return-to-purchase]'
  };

  const TEXT = {
    retailAdd: 'Add to bag',
    b2bAdd: 'Add to wholesale order',
    soldOut: 'Sold out',
    adding: 'Adding…',
    cartError: 'Unable to add these items. Please try again.',
    selectVariant: 'Please select an available size or option.'
  };

  const state = {
    lightbox: null,
    bodyOverflow: ''
  };


  /* =========================================================================
     Utilities
     ========================================================================= */

  const getCurrency = () => {
    return (
      window.Shopify?.currency?.active ||
      document.documentElement.dataset.currency ||
      'AUD'
    );
  };

  const getLocale = () => {
    return document.documentElement.lang || 'en-AU';
  };

  const formatMoney = (cents) => {
    const numeric = Number(cents);

    if (!Number.isFinite(numeric)) return '';

    try {
      return new Intl.NumberFormat(getLocale(), {
        style: 'currency',
        currency: getCurrency(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(numeric / 100);
    } catch (error) {
      return (numeric / 100).toFixed(2);
    }
  };

  const parseJson = (node, fallback = null) => {
    if (!node) return fallback;

    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      console.warn('[MASAL PDP] Unable to parse JSON.', error);
      return fallback;
    }
  };

  const findProductRoot = (element) => {
    if (!element) return null;

    return (
      element.closest(SELECTORS.productRoot) ||
      document.querySelector(SELECTORS.productRoot)
    );
  };

  const getAccountState = (root) => {
    return (
      root?.dataset.customerState ||
      root?.querySelector('[data-masal-account-state]')?.dataset.masalAccountState ||
      root?.querySelector('[data-masal-price-state]')?.dataset.masalPriceState ||
      'guest'
    );
  };

  const setMessage = (form, message = '', type = 'error') => {
    if (!form) return;

    const node = form.querySelector(SELECTORS.formMessage);
    if (!node) return;

    node.textContent = message;
    node.dataset.messageType = message ? type : '';
  };

  const getCartAddUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart/add.js`;
  };

  const getCartUrl = () => {
    return window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cart`
      : '/cart';
  };

  const safeScrollTo = (target, offset = 90) => {
    if (!target) return;

    const top =
      target.getBoundingClientRect().top +
      window.pageYOffset -
      offset;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth'
    });
  };


  /* =========================================================================
     Variant Data / Price Sync
     ========================================================================= */

  const getVariantDataset = (root) => {
    if (!root) return [];

    const jsonNode =
      root.querySelector(SELECTORS.variantsJson) ||
      document.querySelector(SELECTORS.variantsJson);

    return parseJson(jsonNode, []) || [];
  };

  const findVariantById = (root, variantId) => {
    const variants = getVariantDataset(root);

    return variants.find(
      (variant) => String(variant.id) === String(variantId)
    );
  };

  const updatePrice = (root, variant) => {
    if (!root || !variant) return;

    const accountState = getAccountState(root);

    // Guest pages intentionally contain no numeric price data.
    if (accountState === 'guest') return;

    const priceNode = root.querySelector(SELECTORS.price);
    const compareNode = root.querySelector(SELECTORS.comparePrice);

    if (priceNode && variant.price != null) {
      priceNode.textContent = formatMoney(variant.price);
    }

    if (compareNode) {
      const compare = Number(variant.compare_at_price || 0);
      const price = Number(variant.price || 0);
      const showCompare = compare > price;

      compareNode.hidden = !showCompare;
      compareNode.textContent = showCompare ? formatMoney(compare) : '';
    }
  };

  const updatePurchaseButton = (root, variant) => {
    if (!root) return;

    const accountState = getAccountState(root);
    const buttons = root.querySelectorAll(SELECTORS.addButton);

    buttons.forEach((button) => {
      const label = button.querySelector(SELECTORS.addLabel);

      if (!variant) {
        button.disabled = true;
        if (label) label.textContent = TEXT.selectVariant;
        return;
      }

      button.disabled = !variant.available;

      if (label) {
        if (!variant.available) {
          label.textContent = TEXT.soldOut;
        } else if (accountState === 'b2b') {
          label.textContent = TEXT.b2bAdd;
        } else {
          label.textContent = TEXT.retailAdd;
        }
      }
    });
  };

  const syncVariantInput = (root, variantId) => {
    if (!root || !variantId) return;

    root.querySelectorAll(SELECTORS.variantInput).forEach((input) => {
      input.value = variantId;
    });
  };

  const handleVariantChange = (event) => {
    const root = findProductRoot(event.target);
    if (!root) return;

    const variantId = event.detail?.variantId;
    if (!variantId) return;

    syncVariantInput(root, variantId);

    const variant = findVariantById(root, variantId);

    // When full contextual variant JSON is available, update price + CTA.
    if (variant) {
      updatePrice(root, variant);
      updatePurchaseButton(root, variant);
    } else {
      // Minimal variant data from the picker still gives availability.
      updatePurchaseButton(root, {
        id: variantId,
        available: Boolean(event.detail?.available)
      });
    }
  };


  /* =========================================================================
     Add-ons
     ========================================================================= */

  const getSelectedAddons = (root) => {
    if (!root) return [];

    return [...root.querySelectorAll(
      `${SELECTORS.addon}:checked:not(:disabled)`
    )]
      .map((input) => ({
        id: Number(
          input.dataset.masalAddonVariantId ||
          input.value
        ),
        quantity: 1
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0);
  };

  const updateAddonVariant = (select) => {
    if (!select) return;

    const card = select.closest('[data-masal-addon-card]');
    if (!card) return;

    const input = card.querySelector(SELECTORS.addon);
    const selectedOption = select.options[select.selectedIndex];

    if (!input || !selectedOption) return;

    const variantId = select.value;
    const available = selectedOption.dataset.available === 'true';

    input.value = variantId;
    input.dataset.masalAddonVariantId = variantId;
    input.disabled = !available;

    if (!available) input.checked = false;

    card.classList.toggle('is-unavailable', !available);
  };


  /* =========================================================================
     Cart
     ========================================================================= */

  const addItemsToCart = async (items) => {
    const response = await fetch(getCartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data?.description ||
        data?.message ||
        TEXT.cartError
      );
    }

    return data;
  };

  const handleProductSubmit = async (event) => {
    const form = event.target.closest(SELECTORS.productForm);
    if (!form) return;

    // Prevent the older inline MASAL handler from handling the same form twice.
    if (form.dataset.masalExternalJsHandled === 'submitting') {
      event.preventDefault();
      return;
    }

    const root = findProductRoot(form);
    if (!root) return;

    const accountState = getAccountState(root);

    // Guests should never have a purchasable product form.
    if (accountState === 'guest') return;

    const variantInput = form.querySelector(SELECTORS.variantInput);
    const variantId = Number(variantInput?.value || 0);

    if (!Number.isFinite(variantId) || variantId <= 0) {
      event.preventDefault();
      setMessage(form, TEXT.selectVariant);
      return;
    }

    const addons = getSelectedAddons(root);

    // If there are no add-ons, let Shopify / theme handle normal add-to-cart.
    // This preserves drawer-cart behaviour in themes that support it.
    if (!addons.length) return;

    event.preventDefault();

    const addButton = form.querySelector(SELECTORS.addButton);
    const addLabel = addButton?.querySelector(SELECTORS.addLabel);
    const originalLabel = addLabel?.textContent || '';

    form.dataset.masalExternalJsHandled = 'submitting';

    if (addButton) addButton.disabled = true;
    if (addLabel) addLabel.textContent = TEXT.adding;

    setMessage(form, '');

    const items = [
      {
        id: variantId,
        quantity: 1
      },
      ...addons
    ];

    try {
      await addItemsToCart(items);

      // Redirect to cart so the complete gown + add-ons are unmistakably shown.
      window.location.href = getCartUrl();
    } catch (error) {
      console.error('[MASAL PDP] Cart add failed.', error);

      setMessage(
        form,
        error?.message || TEXT.cartError
      );

      form.dataset.masalExternalJsHandled = '';

      if (addButton) addButton.disabled = false;
      if (addLabel) addLabel.textContent = originalLabel;
    }
  };


  /* =========================================================================
     Media Lightbox
     ========================================================================= */

  const createLightbox = () => {
    if (state.lightbox) return state.lightbox;

    const dialog = document.createElement('dialog');
    dialog.className = 'masal-pdp-lightbox';
    dialog.setAttribute('aria-label', 'Product media viewer');

    dialog.innerHTML = `
      <div class="masal-pdp-lightbox__backdrop" data-masal-lightbox-close></div>

      <div class="masal-pdp-lightbox__panel">
        <button
          type="button"
          class="masal-pdp-lightbox__close"
          aria-label="Close product media"
          data-masal-lightbox-close
        >
          ×
        </button>

        <div class="masal-pdp-lightbox__content" data-masal-lightbox-content></div>
      </div>
    `;

    const style = document.createElement('style');

    style.textContent = `
      .masal-pdp-lightbox {
        width: 100vw;
        max-width: none;
        height: 100vh;
        max-height: none;
        margin: 0;
        padding: 0;
        border: 0;
        background: transparent;
        overflow: hidden;
      }

      .masal-pdp-lightbox::backdrop {
        background: rgba(0,0,0,.82);
      }

      .masal-pdp-lightbox__backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0,0,0,.82);
      }

      .masal-pdp-lightbox__panel {
        position: absolute;
        inset: 24px;
        z-index: 2;
        display: grid;
        place-items: center;
        pointer-events: none;
      }

      .masal-pdp-lightbox__content {
        width: min(94vw, 1500px);
        height: min(92vh, 1100px);
        display: grid;
        place-items: center;
        pointer-events: auto;
      }

      .masal-pdp-lightbox__content img,
      .masal-pdp-lightbox__content video,
      .masal-pdp-lightbox__content iframe,
      .masal-pdp-lightbox__content model-viewer {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: contain;
        border: 0;
      }

      .masal-pdp-lightbox__close {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 4;
        width: 44px;
        height: 44px;
        display: grid;
        place-items: center;
        border: 1px solid rgba(255,255,255,.45);
        border-radius: 50%;
        background: rgba(0,0,0,.35);
        color: #fff;
        font: 300 30px/1 Arial, sans-serif;
        cursor: pointer;
        pointer-events: auto;
      }

      @media (max-width: 749px) {
        .masal-pdp-lightbox__panel {
          inset: 12px;
        }

        .masal-pdp-lightbox__content {
          width: 96vw;
          height: 88vh;
        }

        .masal-pdp-lightbox__close {
          width: 40px;
          height: 40px;
        }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(dialog);

    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-masal-lightbox-close]')) {
        closeLightbox();
      }
    });

    dialog.addEventListener('close', () => {
      cleanupLightboxContent(dialog);
      restoreBodyScroll();
    });

    state.lightbox = dialog;

    return dialog;
  };

  const cleanupLightboxContent = (dialog = state.lightbox) => {
    const content = dialog?.querySelector('[data-masal-lightbox-content]');
    if (!content) return;

    content.querySelectorAll('video').forEach((video) => {
      try {
        video.pause();
      } catch (_) {
        // Nothing to do.
      }
    });

    content.innerHTML = '';
  };

  const lockBodyScroll = () => {
    state.bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  };

  const restoreBodyScroll = () => {
    document.body.style.overflow = state.bodyOverflow;
  };

  const closeLightbox = () => {
    const dialog = state.lightbox;
    if (!dialog) return;

    cleanupLightboxContent(dialog);

    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      restoreBodyScroll();
    }
  };

  const cloneMediaForLightbox = (mediaItem) => {
    if (!mediaItem) return null;

    const source =
      mediaItem.querySelector('img') ||
      mediaItem.querySelector('video') ||
      mediaItem.querySelector('iframe') ||
      mediaItem.querySelector('model-viewer');

    if (!source) return null;

    const clone = source.cloneNode(true);

    if (clone.tagName === 'IMG') {
      const currentSrc = source.currentSrc || source.src;
      if (currentSrc) clone.src = currentSrc;

      clone.removeAttribute('loading');
      clone.removeAttribute('sizes');
    }

    if (clone.tagName === 'VIDEO') {
      clone.controls = true;
      clone.autoplay = true;
      clone.muted = true;
      clone.loop = true;
      clone.playsInline = true;
    }

    return clone;
  };

  const openMediaLightbox = (button) => {
    const mediaItem = button?.closest(SELECTORS.mediaItem);
    if (!mediaItem) return;

    const mediaClone = cloneMediaForLightbox(mediaItem);
    if (!mediaClone) return;

    const dialog = createLightbox();
    const content = dialog.querySelector('[data-masal-lightbox-content]');

    cleanupLightboxContent(dialog);
    content.appendChild(mediaClone);

    lockBodyScroll();

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    if (mediaClone.tagName === 'VIDEO') {
      mediaClone.play().catch(() => {});
    }
  };


  /* =========================================================================
     Final CTA
     ========================================================================= */

  const handleReturnToPurchase = (button) => {
    const root =
      findProductRoot(button) ||
      document.querySelector(SELECTORS.productRoot);

    const target =
      root?.querySelector(SELECTORS.productForm) ||
      root?.querySelector(SELECTORS.variantPicker) ||
      root?.querySelector('[data-masal-price-wrapper]') ||
      root;

    safeScrollTo(target, 90);
  };


  /* =========================================================================
     Product Root Initialisation
     ========================================================================= */

  const initialiseRoot = (root) => {
    if (!root || root.dataset.masalProductJsReady === 'true') return;

    root.dataset.masalProductJsReady = 'true';

    const variantInput = root.querySelector(SELECTORS.variantInput);

    if (variantInput?.value) {
      const variant = findVariantById(root, variantInput.value);

      if (variant) {
        updatePrice(root, variant);
        updatePurchaseButton(root, variant);
      }
    }

    root
      .querySelectorAll(SELECTORS.addonVariantSelect)
      .forEach(updateAddonVariant);
  };

  const initialiseAll = (scope = document) => {
    const roots = [];

    if (scope.matches?.(SELECTORS.productRoot)) {
      roots.push(scope);
    }

    scope
      .querySelectorAll?.(SELECTORS.productRoot)
      .forEach((root) => roots.push(root));

    [...new Set(roots)].forEach(initialiseRoot);
  };


  /* =========================================================================
     Delegated Events
     ========================================================================= */

  document.addEventListener('masal:variant-change', handleVariantChange);

  document.addEventListener('change', (event) => {
    const addonVariantSelect = event.target.closest(
      SELECTORS.addonVariantSelect
    );

    if (addonVariantSelect) {
      updateAddonVariant(addonVariantSelect);
    }
  });

  document.addEventListener('submit', handleProductSubmit, true);

  document.addEventListener('click', (event) => {
    const expandButton = event.target.closest(SELECTORS.mediaExpand);

    if (expandButton) {
      event.preventDefault();
      openMediaLightbox(expandButton);
      return;
    }

    const returnButton = event.target.closest(SELECTORS.returnToPurchase);

    if (returnButton) {
      event.preventDefault();
      handleReturnToPurchase(returnButton);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && state.lightbox?.open) {
      closeLightbox();
    }
  });


  /* =========================================================================
     Boot
     ========================================================================= */

  const boot = () => initialiseAll(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }


  /* =========================================================================
     Shopify Theme Editor
     ========================================================================= */

  document.addEventListener('shopify:section:load', (event) => {
    initialiseAll(event.target);
  });

})();